import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UserSettings, UserSettingsUpdate, LLMConfiguration } from '../types';
import { getUserSettings, updateUserSettings, getLLMConfigurations, isAuthenticated } from '../api';
import { updateLanguageFromSettings } from '../i18n';

interface SettingsContextType {
  settings: UserSettings | null;
  llmConfigurations: LLMConfiguration[];
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
  refreshAfterLogin: () => Promise<void>;
  updateSettings: (updates: UserSettingsUpdate) => Promise<void>;
  lastFetch: number | null;
  // LLM convenience methods
  isLLMEnabled: () => boolean;
  isLLMConfigured: () => boolean;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [llmConfigurations, setLLMConfigurations] = useState<LLMConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number | null>(null);
  const initializedRef = useRef(false);
  
  // Use refs to avoid dependency cycles in useCallback
  const settingsRef = useRef(settings);
  const llmConfigurationsRef = useRef(llmConfigurations);
  const lastFetchRef = useRef(lastFetch);
  
  // Update refs when state changes
  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { llmConfigurationsRef.current = llmConfigurations; }, [llmConfigurations]);
  useEffect(() => { lastFetchRef.current = lastFetch; }, [lastFetch]);

  const refreshSettings = useCallback(async () => {
    
    // Always fetch if we don't have settings or llmConfigurations (e.g., after page refresh)
    if (!settingsRef.current || llmConfigurationsRef.current.length === 0) {
      // Force refresh
    } else {
      // Don't fetch if we recently fetched and cache is still valid
      const shouldRefresh = () => {
        if (!lastFetchRef.current) return true;
        return Date.now() - lastFetchRef.current > CACHE_DURATION;
      };
      
      if (!shouldRefresh()) {
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const [fetchedSettings, fetchedLLMConfigurations] = await Promise.all([
        getUserSettings(),
        getLLMConfigurations()
      ]);
      setSettings(fetchedSettings);
      setLLMConfigurations(fetchedLLMConfigurations.configurations);
      setLastFetch(Date.now());
      
      // Apply language settings
      updateLanguageFromSettings(fetchedSettings);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('errors.failedToLoadSettings');
      setError(errorMessage);
      
    } finally {
      setLoading(false);
    }
  }, [t]);

  const updateSettings = useCallback(async (updates: UserSettingsUpdate) => {
    setLoading(true);
    setError(null);

    try {
      const updatedSettings = await updateUserSettings(updates);
      setSettings(updatedSettings);
      setLastFetch(Date.now());
      
      // Apply language settings immediately after update
      updateLanguageFromSettings(updatedSettings);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('errors.failedToUpdateSettings');
      setError(errorMessage);
      
      throw err; // Re-throw so components can handle the error
    } finally {
      setLoading(false);
    }
  }, [t]);

  const refreshAfterLogin = useCallback(async () => {
    initializedRef.current = false; // Reset initialization flag
    await refreshSettings();
  }, [refreshSettings]);

  // Convenience methods for LLM functionality

  const isLLMEnabled = () => {
    return llmConfigurations.length > 0;
  };

  const isLLMConfigured = () => {
    const hasConfigs = llmConfigurations.length > 0;
    const hasDefault = llmConfigurations.some(config => config.is_default);
    return hasConfigs && hasDefault;
  };

  // Initial load when provider mounts (with guard against double-mounting in StrictMode)
  useEffect(() => {
    if (!initializedRef.current && isAuthenticated()) {
      initializedRef.current = true;
      refreshSettings();
    } else {
    }
  }, [refreshSettings, llmConfigurations.length, settings]);

  const value: SettingsContextType = {
    settings,
    llmConfigurations,
    loading,
    error,
    refreshSettings,
    refreshAfterLogin,
    updateSettings,
    lastFetch,
    isLLMEnabled,
    isLLMConfigured,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};